import { useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { LockKeyhole } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverTitle, PopoverTrigger } from '@/components/ui/popover';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { useSession } from './session';

// The header avatar owns selection for every route. The account node is the
// original Clerk/native control, never a child of the avatar's button.
export function ProfileMenu({ account, route }: { account: ReactNode; route: string }) {
  const session = useSession();
  const [open, setOpen] = useState(false), [pin, setPin] = useState(''),
    [pendingProfile, setPendingProfile] = useState<string>(),
    [message, setMessage] = useState(''), [switching, setSwitching] = useState(false);
  const loadingAccount = session.loading && !session.data;
  const accountScope = session.accountScope ?? loadingAccount;
  const owner = useMemo(() => ({ active: false, selection: null as object | null }),
    [session.signedIn, session.data?.user.id, accountScope]);
  useLayoutEffect(() => {
    owner.active = true;
    setOpen(false); setPin(''); setPendingProfile(undefined); setMessage(''); setSwitching(false);
    return () => { owner.active = false; owner.selection = null; };
  }, [owner]);
  useLayoutEffect(() => { setOpen(false); setPin(''); setPendingProfile(undefined); setMessage(''); }, [route]);
  const active = session.data?.profiles.find(p => p.id === session.data?.profileId);
  const profile = session.data?.profiles.find(p => p.id === (pendingProfile || session.data?.profileId));
  async function switchProfile(id: string, suppliedPin?: string) {
    if (!owner.active || owner.selection || !session.data?.profiles.some(p => p.id === id)) return;
    const selection = {}; owner.selection = selection;
    const current = () => owner.active && owner.selection === selection;
    setSwitching(true); setMessage('');
    try {
      await session.selectProfile(id, suppliedPin);
      if (!current()) return;
      setMessage(suppliedPin === undefined ? 'Profile changed.' : 'Profile unlocked.');
      setPin(''); setPendingProfile(undefined);
    } catch (error) {
      if (current()) setMessage(error instanceof Error ? error.message : 'Could not switch profiles. Try again.');
    } finally { if (current()) { owner.selection = null; setSwitching(false); } }
  }
  if (!session.signedIn || !session.data) return <>{account}</>;
  return <Popover open={open} onOpenChange={value => {
    if (!owner.active) return;
    // Closing a popup does not release a pending selection's ownership.
    setOpen(value);
    if (!value) { setPin(''); if (!owner.selection) { setPendingProfile(undefined); setMessage(''); } }
  }}>
    <PopoverTrigger className="profile-avatar" aria-label={`Profiles and account: ${active?.name || 'Streamit'}`}>
      <span aria-hidden="true">{active?.name.slice(0, 1) || 'S'}</span>
    </PopoverTrigger>
    {/* Keep the supplied Clerk control mounted when its own account popup opens
        outside this layer. Closing the profile popup must not destroy it. */}
    <PopoverPrimitive.Portal keepMounted><PopoverPrimitive.Positioner align="end" sideOffset={10} className="isolate z-50">
    <PopoverPrimitive.Popup data-slot="popover-content" className="profile-menu" aria-busy={switching}>
      <PopoverTitle>Profiles and account</PopoverTitle>
      <p className="muted">Active profile: <strong>{active?.name}</strong></p>
      <label>Profile<select value={pendingProfile || session.data.profileId} disabled={switching} onChange={event => {
        if (!owner.active || owner.selection) return;
        const id = event.target.value, selected = session.data!.profiles.find(p => p.id === id);
        if (!selected) return;
        setPin(''); setMessage('');
        if (selected.hasPin) { setPendingProfile(id); setMessage('Enter this profile’s PIN to switch.'); return; }
        setPendingProfile(undefined); void switchProfile(id);
      }}>{session.data.profiles.map(p => <option key={p.id} value={p.id}>{p.name}{p.hasPin ? ' 🔒' : ''}</option>)}</select></label>
      {profile?.hasPin && <form onSubmit={event => { event.preventDefault(); void switchProfile(profile.id, pin); }}>
        <label><LockKeyhole size={16} aria-hidden="true" />Profile PIN<Input aria-label="Profile PIN" type="password" inputMode="numeric" maxLength={8}
          disabled={switching} value={pin} onChange={event => { if (owner.active) setPin(event.target.value); }} autoComplete="off" /></label>
        <Button variant="outline" type="submit" disabled={switching}>Unlock</Button>
      </form>}
      {(message || switching) && <p role="status">{switching ? 'Switching profile…' : message}</p>}
      <div className="profile-account"><span>Sign-in and account</span>{account}</div>
    </PopoverPrimitive.Popup></PopoverPrimitive.Positioner></PopoverPrimitive.Portal>
  </Popover>;
}
